package com.crowpay.views.components.project.work.payApps

import com.crowpay.PayApplication
import com.crowpay.Project
import com.crowpay.ScopeView
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.Formats
import com.crowpay.format
import com.crowpay.utils.renderDollars
import com.crowpay.utils.tables.trackWeights
import com.crowpay.views.components.infoBubble
import com.crowpay.views.components.scopeSetIcon
import com.crowpay.views.screens.common.DialogOrPage
import com.crowpay.views.theming.*
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.minus
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.ReactiveContext
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import kotlinx.datetime.Instant
import kotlin.math.roundToLong

class PayAppSummary(
    val payApp: PayApplication,
    val project: Project,
    val scopes: List<ScopeView>,
    val items: List<LineItemWithPaymentInfo>
) : DialogOrPage {

    val groupedItems = items
        .groupBy { it.lineItem.scopeView }
        .mapKeys { (id, _) -> scopes.find { it._id == id } }
        .entries.sortedBy { it.key?._id }

    val thisDraw = items.mapNotNull { it.payApp }.sumOf { it.amount }
    val totalWorkCompleted = items.sumOf { it.previousPayments } + thisDraw

    private fun ViewWriter.contents() = col {
        spacing = AppDimensions.majorColumnSpacing

        var timestamp: Instant? = null
        for (item in items) {
            item.payApp?.submittedOrReviewed?.let {
                if (timestamp == null) timestamp = it
                else if (it < timestamp!!) timestamp = it
            }
        }
        if (timestamp == null) timestamp = payApp.created

        title("Draw Billing Summary")

        atStart - sizeConstraints(width = 25.rem) - lightSection - col {
            val valueSpacing = 4.dp
            val rowSpacing = 1.rem

            fun RowOrCol.tableRow(
                key: String,
                bold: Boolean? = false,
                value: ReactiveContext.() -> String,
                helper: (ReactiveContext.() -> String)?
            ) {
                col {
                    spacing = valueSpacing
                    row {
                        spacing = valueSpacing
                        expanding - centered - if (bold == true) bodyBold(key) else body(key)
                        text {
                            themeChoice += if (bold == true) BodySemantic + BoldSemantic else BodySemantic
                            align = Align.End
                            ::content.invoke(value)
                        }
                    }
                    if (helper != null) smallBody {
                        ::content.invoke(helper)
                    }
                }
            }

            col {
                spacing = rowSpacing - valueSpacing
                tableRow(
                    key = "Draw Work Value",
                    value = { thisDraw.renderDollars() },
                    helper = null
                )
                tableRow(
                    key = "Less Retention",
                    value = {
                        (thisDraw * project.safeRetention).roundToLong().renderDollars()
                    },
                    helper = null
                )
                tableRow(
                    key = "Amount Due Request",
                    bold = true,
                    value = { (thisDraw - (thisDraw * project.safeRetention).roundToLong()).renderDollars() },
                    helper = { "(Money to be Released to Contractor)" }
                )
            }
        }

        title("Draw ${payApp.number}: ${timestamp.format(Formats.mmddyyyy)}")

        sizeConstraints(width = 85.rem) - lightSection - col {
            trackWeights {
                row {
                    fun ViewWriter.columnLabel(
                        info: String,
                        content: RowOrCol.() -> ViewModifiable
                    ) =
                        stack {
                            centered - row {
                                spacing = 5.dp
                                centered - infoBubble(info)
                                centered - content()
                                space(0.0)
                            }
                        }

                    fun ViewWriter.columnLabel(name: String, info: String) = columnLabel(info) {
                        bodyBold {
                            wraps = true
                            content = name
                        }
                    }

                    setColWeight(2f) - atBottomStart - bodyBold("Work Item")
                    setColWeight(1.2f) - atBottomCenter - columnLabel("Approved", "Approved work including changes")
                    setColWeight(1.2f) - atBottomCenter - columnLabel("Processed", "Draw items that have been paid and work items that have been signed off as done.")
                    setColWeight(1.2f) - atBottomCenter - columnLabel("Pending", "Work items that are in Request Sign-Off status or draw items that are open.")
                    setColWeight(1f) - atBottomCenter - columnLabel("Draw", "Value of work being submitted for this draw.")
                    setColWeight(1.5f) - atBottomCenter - columnLabel("All work values from processed, pending, and this draw") {
                        bodyBold("Work To-Date")
                        smallBody("(%)")
                    }
                    setColWeight(1f) - atBottomCenter - columnLabel("Remaining", "Remaining work value on that work item.")
                    setColWeight(1.5f) - atBottomCenter - columnLabel("The portion of retention total allocated to this item based on Work To-Date.") {
                        atBottomStart - bodyBold {
                            wraps = true
                            ::content { "Retention Portion (${project.safeRetention.times(100).roundToLong()}%)" }
                        }
                    }

//                                fun ViewWriter.header(text: String) = bodyBold {
//                                    align = Align.Center
//                                    content = text
//                                }
//                                setColWeight(2.2f) - atBottomCenter - bodyBold("Work Item")
//                                setColWeight(1f) - atBottomCenter - header("Approved")
//                                setColWeight(1f) - atBottomCenter - header("Processed")
//                                setColWeight(1f) - atBottomCenter - header("Pending")
//                                setColWeight(1f) - atBottomCenter - header("Draw")
//                                setColWeight(1.5f) - atBottomCenter - stack {
//                                    centered - row {
//                                        spacing = 5.dp
//                                        bodyBold("Work To-Date")
//                                        smallBody("(%)")
//                                    }
//                                }
//                                setColWeight(1f) - atBottomCenter - header("Remaining")
//                                setColWeight(1.1f) - atBottomCenter - header(
//                                    "Retention Portion (${
//                                        project.safeRetention.times(
//                                            100
//                                        ).roundToInt()
//                                    }%)"
//                                )
                }
                greySeparator()
                for ((scope, lines) in groupedItems) {
                    if (scope != null) col {
                        space(0.25)
                        compact - section - row {
                            if (scope.scopeSet) centered - scopeSetIcon(1.rem)
                            body(scope.scopeTitle)
                        }
                    }
                    for (line in lines) {
                        row {
                            if (line.lineItem.cancelled) themeChoice += GreyedOutSemantic

                            fun ViewWriter.money(amount: Long?) = body {
                                align = Align.Center
                                content = amount?.renderDollars() ?: "-"
                            }

                            getColWeight - centered - row {
                                spacing = 0.5.rem
                                body(line.name)
                                line.lineItem.changeType?.let {
                                    centered - smallBody(it.postTense)
                                }
                            }
                            getColWeight - centered - money(line.price)
                            getColWeight - centered - money(line.previousPayments)
                            getColWeight - centered - money(line.pending)
                            getColWeight - centered - money(line.thisPayment)
                            getColWeight - centered - stack {
                                centered - row {
                                    spacing = 3.dp
                                    centered - body(line.workCompleted.renderDollars())
                                    centered - smallBody(
                                        if (line.lineItem.cancelled) "(-)"
                                        else "(${line.percentComplete}%)"
                                    )
                                }
                            }
                            getColWeight - centered - money(line.let { it.price - it.workCompleted })
                            getColWeight - centered - money(line.retentionToDate(project.safeRetention))
                        }
                        greySeparator()
                    }
                }
                row {
                    fun ViewWriter.moneyBold(amount: Long) = bodyBold {
                        align = Align.Center
                        content = amount.renderDollars()
                    }
                    getColWeight - bodyBold("Totals")
                    getColWeight - moneyBold(items.sumOf { it.price })
                    getColWeight - moneyBold(items.sumOf { it.previousPayments })
                    getColWeight - moneyBold(items.sumOf { it.pending })
                    getColWeight - moneyBold(items.sumOf { it.thisPayment ?: 0 })
                    getColWeight - centered - stack {
                        centered - row {
                            spacing = 3.dp
                            centered - bodyBold(items.sumOf { it.workCompleted }.renderDollars())
                            centered - smallBody("(${items.percentComplete()}%)")
                        }
                    }
                    getColWeight - moneyBold(items.sumOf { it.price - it.workCompleted })
                    getColWeight - moneyBold(items.retentionToDate(project.safeRetention))
                }
            }
        }

        atStart - secondaryButton - button {
            specCenteredText("Close")
            onClick("Close") { dialogScreenNavigator.dismiss() }
        }
    }

    override fun ViewWriter.renderAsDialog(): ViewModifiable = dismissBackground {
        spacing = AppDimensions.fullIndent
        scrolls - centered - dialog - stack {
            spacing = AppDimensions.fullIndent
            removeOutline - contents()
        }
    }

    override fun ViewWriter.renderAsPage(): ViewModifiable = col {
        notificationBar(AppColors.yellow) - body("For the best experience, view this information on a larger screen.")
        expanding - scrollsBoth {} - contents()
    }
}